/**
 * Canonical Opportunity Value & Rep Resolvers
 * ───────────────────────────────────────────
 * Closed Won opportunities MUST resolve customer-facing value and rep
 * identity from their linked Job — the canonical, change-order-aware
 * source of truth. Snapshot fields on `Opportunity` (`totalEstimate`,
 * `assignedRep`) remain audit-only fallbacks for unlinked opportunities
 * (active pipeline, Closed Lost, or pre-job records).
 *
 * Revenue (vs display) drops to 0 when a Won deal didn't realize cash:
 * canceled, finance_declined, or pending rescission.
 *
 * See mem://finance/canonical-pricing and mem://architecture/canonical-data-sources.
 */
import type { Opportunity } from "./opportunityTypes";
import type { Job } from "./types";
import { getJobDisplayValue } from "./jobPricingSelectors";

/** Resolve linked job for an opportunity, if any. */
function findLinkedJob(opp: Opportunity, jobs: Job[]): Job | undefined {
  if (!opp.linkedJobId) return undefined;
  return jobs.find((j) => j.id === opp.linkedJobId);
}

/**
 * Customer-facing display value.
 * Linked → canonical job contract value. Unlinked → opportunity snapshot.
 */
export function getOpportunityDisplayValue(opp: Opportunity, jobs: Job[]): number {
  const job = findLinkedJob(opp, jobs);
  if (job) return getJobDisplayValue(job);
  return opp.totalEstimate || 0;
}

/**
 * Realized revenue value.
 * Mirrors display value, but returns 0 when the Won outcome did not
 * produce realized revenue (canceled, finance_declined, or pending
 * rescission). Non-Won opportunities are always 0.
 */
export function getOpportunityRevenueValue(opp: Opportunity, jobs: Job[]): number {
  if (opp.stage !== "Closed Won") return 0;
  const outcome = opp.wonOutcome;
  // pending = Pending — Rescission (not yet realized)
  if (outcome === "canceled" || outcome === "finance_declined" || outcome === "pending") {
    return 0;
  }
  return getOpportunityDisplayValue(opp, jobs);
}

/**
 * Canonical sales rep for an opportunity.
 * Linked → linked job's salesRep. Unlinked → opportunity snapshot.
 */
export function getOpportunityRep(opp: Opportunity, jobs: Job[]): string {
  const job = findLinkedJob(opp, jobs);
  if (job?.salesRep) return job.salesRep;
  return opp.assignedRep || "Unassigned";
}
